import React, { memo, useMemo } from 'react';
import {
  Button,
  Callout,
  CalloutProps,
  Divider,
  Dropdown,
  DropdownOption,
  FlexBox,
  FormField,
  FormInputGroup,
  FormLabel,
  GridContainer,
  Icon,
  Link,
  StandardForm,
  TextInput,
  Typography,
} from '@vp/swan';
import { addQueryParam, removeQueryParam } from 'src/utils/QueryStringUtil';
import {
  computeGateSources,
  FAKE_ID,
  FEATURE_FLAG_COOKIE,
  GateSource,
  loadFeatureGates,
  readOverrides,
  serialize,
  setOverrides,
} from './util';

const inputStyle: React.CSSProperties = {
  clip: 'none',
  clipPath: 'none',
  height: 'initial',
  width: '100%',
  margin: 'var(--swan-sem-space-none)',
  overflow: 'none',
  padding: 'var(--swan-sem-space-4)',
  position: 'relative',
};

const gateNameStyle: React.CSSProperties = {
  flex: 1,
};

const StatsigFeatureGatesContentsInner = () => {
  const [version, gates] = useMemo(() => loadFeatureGates(), []);
  // Internal mutable collections
  // - `gates`: full display list (placeholders + remote + persisted overrides + query params)
  // - `overridesRef`: a mutable list containing ONLY the developer overrides that are actually
  //    persisted in localStorage under `FEATURE_FLAG_OVERRIDES_KEY`. We read from that dedicated
  //    key so seeded placeholders (which use the same TEST sentinel id) are NOT treated as persisted overrides.
  const initialOverrides = React.useMemo(() => {
    try {
      return readOverrides();
    } catch {
      return [] as string[];
    }
  }, []);
  const overridesRef = React.useRef<string[]>(initialOverrides);

  // Persist only the developer overrides into the dedicated overrides key. `serialize` expects
  // the gates array to already contain only serialized entries (name^id^value). The helper
  // `setOverrides` writes the raw serialized payload to localStorage.
  const persistOverrides = () => {
    const minimal = overridesRef.current;
    setOverrides(serialize(version, minimal));
  };

  const baseSnapshotMap = useMemo((): Record<string, string> => {
    try {
      // Build baseline (remote) map out of the legacy local copy under the cookie key.
      // We prefer to read the dedicated cookie via the util helper, but this component keeps
      // a very small, self-contained parser for the legacy localStorage snapshot to avoid
      // importing additional modules into the UI bundle.
      const raw = window.localStorage.getItem(FEATURE_FLAG_COOKIE);
      if (!raw) {
        return {};
      }
      const [, list] = raw.split('~');
      if (!list) {
        return {};
      }
      return list.split('|').reduce<Record<string, string>>((acc, entry) => {
        const [n, id, val] = entry.split('^');
        if (n && id && val !== undefined) {
          acc[n] = `${id}^${val}`;
        }
        return acc;
      }, {});
    } catch {
      return {};
    }
  }, []);
  const sources = useMemo(() => computeGateSources() as Record<string, GateSource>, []);

  const sourceBadge: Record<GateSource, { label: string; skin: CalloutProps['skin'] }> = {
    QUERY_PARAM: {
      label: 'QP',
      skin: 'accent',
    },
    OVERRIDE: {
      label: 'OVR',
      skin: 'warning',
    },
    REMOTE: {
      label: 'REM',
      skin: 'success',
    },
    PLACEHOLDER: {
      label: 'DEF',
      skin: 'standard',
    },
  };

  const onChange = (gate: string, enabled: string) => {
    if (enabled === 'default') {
      curryOnReset(gate)();
    } else {
      const index = gates.indexOf(gate);

      if (index !== -1) {
        const [name, id] = gate.split('^');
        if (name === undefined || id === undefined) {
          return;
        }

        const value = `${id}^${enabled}`; // id is original id (could be TEST or remote id)
        const nextEntry = `${name}^${value}`;
        gates[index] = nextEntry;

        // Decide if this should be in overrides (always store TEST entries only differing from base)
        const baseEntry = baseSnapshotMap[name];
        const baseValue = baseEntry ? baseEntry.split('^')[1] : undefined;
        const isMeaningful = baseValue !== enabled;
        // Ensure override stored with TEST id (so we can control independent of remote id)
        const overrideKey = `${name}^${FAKE_ID}^${enabled}`;
        overridesRef.current = overridesRef.current.filter(o => !o.startsWith(name + '^'));
        if (isMeaningful) {
          overridesRef.current.push(overrideKey);
        }
        persistOverrides();

        window.location.href = addQueryParam(window.location.href, name, enabled);
      }
    }
  };

  const curryOnReset = (gate: string) => () => {
    const index = gates.indexOf(gate);

    if (index !== -1) {
      gates.splice(index, 1);
      // Remove any override tracking for this gate
      const [resetName] = gate.split('^');
      overridesRef.current = overridesRef.current.filter(o => !o.startsWith(resetName + '^'));
      persistOverrides();

      window.location.href = removeQueryParam(window.location.href, resetName);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = (event.currentTarget[0] as HTMLInputElement).value;
    const enabled = (event.currentTarget[1] as HTMLSelectElement).value;

    if (!name) {
      return;
    }

    if (enabled === 'default') {
      curryOnReset(name)();
    } else {
      const value = `${FAKE_ID}^${enabled}`;
      const entry = `${name}^${value}`;

      gates.push(entry);
      // Add explicit override regardless of base
      overridesRef.current = overridesRef.current.filter(o => !o.startsWith(name + '^'));
      overridesRef.current.push(entry);
      persistOverrides();

      window.location.href = addQueryParam(window.location.href, name, enabled);
    }
  };

  return (
    <GridContainer>
      <StandardForm skin="tight" variant="horizontal" mb={2} onSubmit={handleSubmit}>
        <FlexBox alignItems="flex-end" justifyContent="space-between" flexDirection="row" flexWrap="nowrap" gap={4}>
          <FormField style={{ width: '50%' }}>
            <FormLabel textAlign="left" mt={0}>
              Feature Gate Name
            </FormLabel>
            <FormInputGroup>
              <TextInput
                placeholder="Start typing..."
                // Resetting style since any input inside a buttonbar is forcibly hidden
                style={inputStyle}
                size="mini"
              />
            </FormInputGroup>
          </FormField>
          <FormField style={{ width: '25%' }}>
            <FormLabel textAlign="left" mt={0}>
              Enabled?
            </FormLabel>
            <FormInputGroup>
              <Dropdown fullWidth>
                <DropdownOption value="false">False</DropdownOption>
                <DropdownOption value="true">True</DropdownOption>
                <DropdownOption value="default">Default</DropdownOption>
              </Dropdown>
            </FormInputGroup>
          </FormField>
          <FormField mb={5}>
            <Button type="submit" skin="secondary" iconPosition="left" style={{ border: '1px solid white' }}>
              <Icon alt="add" iconType="add" />
              Add
            </Button>
          </FormField>
        </FlexBox>
      </StandardForm>
      <Divider mb={2} />
      {gates.length === 0 && <Typography>No active feature flags</Typography>}
      {gates.map(flag => {
        const parts = flag.split('^');
        const name: string = parts[0] ?? '';
        const currentValue = parts[2];
        const source: GateSource = sources[name] ?? 'PLACEHOLDER';
        const badge = sourceBadge[source];

        return (
          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            key={name}
            className="studio-debug-toolbar-flags"
            flexWrap="nowrap"
            gap={3}
            mb={2}
          >
            <FlexBox alignItems="center" gap={2} style={gateNameStyle}>
              <Link
                skin="unstyled"
                title="Open in Statsig"
                href={`https://console.statsig.com/5Yx4xlMFVM9VTFvZaUwBnI/gates/${name}/`}
                target="_blank"
                marginRight={'5'}
              >
                <Typography component="code">{name}</Typography>
              </Link>
              {badge && (
                <Callout skin={badge.skin} title={`Source: ${source}`} ml={'auto'} mr={2}>
                  {badge.label}
                </Callout>
              )}
            </FlexBox>
            <Dropdown value={currentValue} fontSkin="body-small" onChange={event => onChange(flag, event.target.value)}>
              <DropdownOption value="false">False</DropdownOption>
              <DropdownOption value="true">True</DropdownOption>
              <DropdownOption value="default">Default</DropdownOption>
            </Dropdown>
            <Button buttonShape="round" onClick={curryOnReset(flag)}>
              <Icon alt="reset" iconType="reset" />
            </Button>
          </FlexBox>
        );
      })}
    </GridContainer>
  );
};

export const StatsigFeatureGatesContents = memo(StatsigFeatureGatesContentsInner);

StatsigFeatureGatesContents.displayName = 'StatsigFeatureGatesContents';
